if (!customElements.get('sticky-add-to-cart-bar')) {
  customElements.define(
    'sticky-add-to-cart-bar',
    class StickyAddToCartBar extends HTMLElement {
      constructor() {
        super();

        this.onChange = this.handleChange.bind(this);
        this.onVariantChange = this.handleVariantChange.bind(this);
        this.isVisible = false;
      }

      connectedCallback() {
        this.productInfo = this.closest('product-info');
        this.sentinel = document.querySelector(this.dataset.sentinelSelector);
        this.addEventListener('change', this.onChange);
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, this.onVariantChange);
        }

        this.observeVisibility();
      }

      disconnectedCallback() {
        this.removeEventListener('change', this.onChange);
        this.visibilityObserver?.disconnect();
        this.resizeObserver?.disconnect();
        window.cancelAnimationFrame(this.heightUpdateFrame);
        this.variantChangeUnsubscriber?.();
        this.setVisible(false);
      }

      observeVisibility() {
        if (!this.sentinel || !('IntersectionObserver' in window)) {
          this.setVisible(false);
          return;
        }

        this.visibilityObserver = new IntersectionObserver(
          ([entry]) => {
            const viewportTop = entry.rootBounds?.top ?? 0;
            const hasPassedSentinel = entry.boundingClientRect.top < viewportTop;
            this.setVisible(entry.isIntersecting || hasPassedSentinel);
          },
          { rootMargin: '0px 0px -200px 0px', threshold: 0 }
        );
        this.visibilityObserver.observe(this.sentinel);
      }

      observeHeight() {
        if (!('ResizeObserver' in window)) return;

        this.resizeObserver ??= new ResizeObserver(([entry]) => {
          const borderBox = Array.isArray(entry.borderBoxSize) ? entry.borderBoxSize[0] : entry.borderBoxSize;
          const height = borderBox?.blockSize ?? entry.contentRect.height;

          window.cancelAnimationFrame(this.heightUpdateFrame);
          this.heightUpdateFrame = window.requestAnimationFrame(() => {
            if (!this.isVisible) return;
            const value = `${Math.ceil(height)}px`;
            if (this.lastHeight === value) return;
            this.lastHeight = value;
            this.style.setProperty('--sticky-atc-height', value);
            document.documentElement.style.setProperty('--sticky-atc-height', value);
          });
        });
        this.resizeObserver.observe(this);
      }

      setVisible(visible) {
        if (this.isVisible === visible) return;

        this.isVisible = visible;
        if (visible) this.observeHeight();
        else {
          this.resizeObserver?.disconnect();
          window.cancelAnimationFrame(this.heightUpdateFrame);
        }
        this.classList.toggle('is-visible', visible);
        this.setAttribute('aria-hidden', visible ? 'false' : 'true');
        this.toggleAttribute('inert', !visible);
        document.body.classList.toggle('carson-sticky-atc-visible', visible);
      }

      handleChange(event) {
        if (!event.target.matches('.carson-sticky-atc__select')) return;

        const selectedOption = event.target.selectedOptions[0];
        if (!selectedOption) return;

        this.updateFromOption(selectedOption);
        this.syncMainVariantPicker(selectedOption);
      }

      updateFromOption(option) {
        const variantIdInput = this.querySelector('input[name="id"]');
        const quantityInput = this.querySelector('.carson-sticky-atc__quantity');
        const price = this.querySelector('.carson-sticky-atc__price');
        const image = this.querySelector('.carson-sticky-atc__image');
        const submitButton = this.querySelector('.carson-sticky-atc__button');
        const submitText = submitButton?.querySelector('span');
        const isAvailable = option.dataset.available === 'true';

        if (variantIdInput) {
          variantIdInput.value = option.value;
          variantIdInput.dispatchEvent(new Event('change', { bubbles: true }));
        }

        if (quantityInput && option.dataset.quantityMin) {
          quantityInput.value = option.dataset.quantityMin;
        }

        if (price && option.dataset.price) {
          price.innerHTML = this.renderPrice(option);
        }

        if (image && option.dataset.image) {
          image.src = option.dataset.image;
          image.removeAttribute('srcset');
        }

        if (submitButton) {
          submitButton.toggleAttribute('disabled', !isAvailable);
          submitButton.setAttribute('aria-disabled', isAvailable ? 'false' : 'true');
        }

        if (submitText) {
          submitText.textContent = isAvailable
            ? window.variantStrings?.addToCart || 'Add to cart'
            : window.variantStrings?.soldOut || 'Sold out';
        }
      }

      renderPrice(option) {
        if (!option.dataset.compareAtPrice) {
          return `<span class="carson-sticky-atc__price-current">${option.dataset.price}</span>`;
        }

        return `
          <span class="visually-hidden">${window.variantStrings?.salePrice || 'Sale price'}</span>
          <span class="carson-sticky-atc__price-current carson-sticky-atc__price-current--sale">${option.dataset.price}</span>
          <span class="visually-hidden">${window.variantStrings?.regularPrice || 'Regular price'}</span>
          <s class="carson-sticky-atc__price-compare">${option.dataset.compareAtPrice}</s>
        `;
      }

      syncMainVariantPicker(option) {
        const variantSelects = this.productInfo?.querySelector('variant-selects');
        const selectedOptions = this.parseSelectedOptions(option);

        if (!variantSelects || !selectedOptions.length) return;

        let changeTarget = null;

        selectedOptions.forEach((value, index) => {
          const group =
            variantSelects.querySelector(`#Option-${this.dataset.sectionId}-${index}`) ||
            variantSelects.querySelector(`fieldset:nth-of-type(${index + 1})`);

          if (!group) return;

          if (group.matches('select')) {
            Array.from(group.options).forEach((selectOption) => {
              const isSelected = selectOption.value === value;
              selectOption.selected = isSelected;
              selectOption.toggleAttribute('selected', isSelected);
            });
            changeTarget = group;
            return;
          }

          const input = group.querySelector(`input[value="${CSS.escape(value)}"]`);
          if (input) {
            input.checked = true;
            changeTarget = input;
          }
        });

        changeTarget?.dispatchEvent(new Event('change', { bubbles: true }));
      }

      parseSelectedOptions(option) {
        try {
          return JSON.parse(option.dataset.options || '[]');
        } catch {
          return [];
        }
      }

      handleVariantChange(event) {
        if (event?.data?.sectionId !== this.dataset.sectionId) return;

        const source = event.data.html?.querySelector(`#${CSS.escape(this.id)}`);
        if (!source) return;

        this.innerHTML = source.innerHTML;
      }
    }
  );
}
